import { useState } from 'react';
import { Link } from 'react-router-dom';
import * as RadixDialog from '@radix-ui/react-dialog';
import { api, get, post, put } from '../api';
import { toast } from '../components/toast';
import { dayLabel } from '../dates';
import { describe } from '../errors';
import { useLoad } from '../hooks';
import { CloseIcon } from '../components/NavIcons';
import { assignmentRef, visitRef } from '../numbers';
import { FLAG, availability, statusOf, type RosterEntry, type ScheduleVisit } from '../visitStatus';

interface Detail {
  id: number;
  number?: number;
  assignment_id: number;
  assignment_number?: number;
  date: string;
  start_time: string;
  end_time: string;
  open: boolean;
  reason: string;
  customer_name: string;
  client_name: string;
  address: { line1: string; line2: string; locality: string; region: string; postcode: string };
  access_notes: string;
  lead_name: string;
  team: {
    id: number;
    name: string;
    is_lead: boolean;
    accepted_at: string | null;
    phone?: string;
  }[];
  holiday: string | null;
  charge: string;
  pay: string;
  cancellation_reason: string;
  reinstate_block: string;
  checklist: { title: string; done: boolean }[];
  notes: { id: number; body: string; from: string }[];
  can_complete: boolean;
  complete_hint: string;
}

/**
 * The job at a glance, opened from the calendar: details, status and what the office does
 * most (give it a lead, confirm for the lead, complete, cancel). Everything else is on the
 * single visit's own page.
 */
export function VisitPanel(props: {
  id: number;
  /** The calendar's copy of this visit, when it is in view; it carries the flags. */
  item?: ScheduleVisit;
  /** Everyone who could take it, and the day's other visits, to say who is free. */
  roster?: RosterEntry[];
  sameDay?: ScheduleVisit[];
  onClose(): void;
  onChanged(): void;
}) {
  const [tick, setTick] = useState(0);
  const loaded = useLoad(() => get<Detail>(`/api/visits/${props.id}`), [props.id, tick]);
  const v = loaded.data;
  const [problems, setProblems] = useState<string[]>([]);
  const [cancelling, setCancelling] = useState(false);
  const [chargeStill, setChargeStill] = useState(true);
  const [payStill, setPayStill] = useState(true);
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);

  async function run(fn: () => Promise<unknown>, done: string) {
    setProblems([]);
    setBusy(true);
    try {
      await fn();
      toast(done);
      setCancelling(false);
      setTick((t) => t + 1);
      props.onChanged();
    } catch (err) {
      setProblems(describe(err).problems);
    } finally {
      setBusy(false);
    }
  }

  const flags = props.item?.flags ?? [];
  // "Changed" is history, not something to act on, so the panel leaves it out.
  const warnings = flags.filter((f) => FLAG[f] && f !== 'holiday' && f !== 'changed');
  const status = v
    ? statusOf({ state: v.open ? 'active' : 'inactive', reason: v.reason, flags })
    : null;
  const address = v
    ? [
        v.address.line1,
        v.address.line2,
        `${v.address.locality} ${v.address.region} ${v.address.postcode}`,
      ]
        .map((s) => s.trim())
        .filter(Boolean)
    : [];
  const done = v?.checklist.filter((c) => c.done).length ?? 0;
  // The lead has not confirmed: the office can do it for them, say after a phone call.
  const lead = v?.team.find((p) => p.is_lead);
  const firstName = (name: string) => name.trim().split(/\s+/)[0] ?? name;
  // The lead marked time off then: the job is to hand it on, not to confirm for them.
  const leadOff =
    v?.open && lead && flags.includes('time_off') && props.item?.time_off_names?.includes(lead.name)
      ? lead
      : null;
  const confirmFor = v?.open && lead && !lead.accepted_at && !leadOff ? lead : null;
  // Who could take it, from the calendar; opened from a link to another week, there is no list.
  const canGive = Boolean(v?.open && props.item && props.roster?.length);
  const needsLead = Boolean(v?.open && !lead);
  const reopenable = !v?.open && (v?.reason === 'cancelled' || v?.reason === 'missed');

  return (
    <RadixDialog.Root open onOpenChange={(open) => !open && props.onClose()}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="backdrop side" />
        <RadixDialog.Content
          className="side-panel"
          aria-describedby={undefined}
          // Back to the visit that was opened, which the calendar marks with data-visit.
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            document.querySelector<HTMLElement>(`[data-visit="${props.id}"]`)?.focus();
          }}
        >
          <div className="row">
            <RadixDialog.Title asChild>
              <h2>{v?.customer_name ?? props.item?.customer_name ?? 'Visit'}</h2>
            </RadixDialog.Title>
            <RadixDialog.Close className="quiet icon-button" aria-label="Close">
              <CloseIcon />
            </RadixDialog.Close>
          </div>

          {loaded.loading && !v && <p>Loading…</p>}
          {loaded.error && (
            <p role="alert" className="error">
              {loaded.error}
            </p>
          )}

          {v && status && (
            <div className="stack">
              <p className="muted">
                {v.number ? `${visitRef(v.number)} · ` : ''}
                {v.client_name && `${v.client_name} · `}
                {dayLabel(v.date)} · {v.start_time}–{v.end_time}
              </p>
              <p>
                <span className={`status-pill tone-${status.tone}`}>{status.label}</span>
              </p>
              {v.cancellation_reason && (
                <p className="muted">Reason given: {v.cancellation_reason}</p>
              )}

              {(warnings.length > 0 || v.holiday) && (
                <ul className="warnings" aria-label="Things to check">
                  {warnings.map((f) => (
                    <li key={f}>
                      {f === 'time_off' && props.item?.time_off_names?.length
                        ? `${props.item.time_off_names.join(' and ')} marked time off then. Assign someone else.`
                        : FLAG[f]!.says}
                    </li>
                  ))}
                  {v.holiday && <li>Public holiday: {v.holiday}.</li>}
                </ul>
              )}

              <dl className="facts">
                <dt>Address</dt>
                <dd>
                  {address.map((l) => (
                    <div key={l}>{l}</div>
                  ))}
                  {address.length > 0 && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address.join(', '))}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open in maps
                    </a>
                  )}
                </dd>
                {v.access_notes && (
                  <>
                    <dt>Access</dt>
                    <dd>{v.access_notes}</dd>
                  </>
                )}
                <dt>Team</dt>
                <dd>
                  {v.team.length === 0 && 'Nobody yet'}
                  {v.team.map((p) => (
                    <div key={p.id}>
                      {p.name} <span className="muted">({p.is_lead ? 'lead' : 'assist'})</span>
                      {p.accepted_at ? ' · confirmed' : ''}
                    </div>
                  ))}
                </dd>
                {v.checklist.length > 0 && (
                  <>
                    <dt>Checklist</dt>
                    <dd>
                      {done} of {v.checklist.length} done
                    </dd>
                  </>
                )}
                <dt>Money</dt>
                <dd>
                  Charge ${v.charge} · Pay ${v.pay}
                </dd>
                {v.notes.length > 0 && (
                  <>
                    <dt>Latest note</dt>
                    <dd>
                      {v.notes[0]!.body} <span className="muted">— {v.notes[0]!.from}</span>
                    </dd>
                  </>
                )}
              </dl>

              {problems.length > 0 && (
                <p role="alert" className="error">
                  {problems.join(' ')}
                </p>
              )}

              {!cancelling && canGive && (needsLead || leadOff) && (
                <GiveTo
                  title={leadOff ? 'Hand it to someone else' : 'Give it a lead'}
                  hint={
                    leadOff
                      ? `${leadOff.name} is off then. Who you pick becomes the lead, and ${firstName(leadOff.name)} comes off this visit.`
                      : 'Who is free then comes first. They become the lead and are asked to confirm.'
                  }
                  visit={props.item!}
                  roster={props.roster!.filter(
                    (r) => !v.team.some((p) => p.id === r.id && p.is_lead),
                  )}
                  sameDay={props.sameDay ?? []}
                  busy={busy}
                  onPick={(r) =>
                    void run(
                      async () => {
                        await put(`/api/visits/${v.id}/lead`, { contractor_id: r.id });
                        if (leadOff)
                          await api('DELETE', `/api/visits/${v.id}/contractors/${leadOff.id}`);
                      },
                      leadOff
                        ? `${r.name} has it now, instead of ${leadOff.name}.`
                        : `${r.name} is now the lead.`,
                    )
                  }
                />
              )}

              {cancelling ? (
                <section className="panel stack" aria-label="Cancel this visit">
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={chargeStill}
                      onChange={(e) => setChargeStill(e.target.checked)}
                    />
                    Still charge the client
                  </label>
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={payStill}
                      onChange={(e) => setPayStill(e.target.checked)}
                    />
                    Still pay the contractor
                  </label>
                  <label>
                    Reason (optional)
                    <input value={why} maxLength={200} onChange={(e) => setWhy(e.target.value)} />
                  </label>
                  <div className="inline">
                    <button
                      type="button"
                      className="danger"
                      disabled={busy}
                      onClick={() =>
                        void run(
                          () =>
                            post(`/api/visits/${v.id}/cancel`, {
                              reason: 'cancelled',
                              chargeable: chargeStill,
                              payable: payStill,
                              why,
                            }),
                          'Visit cancelled.',
                        )
                      }
                    >
                      Cancel the visit
                    </button>
                    <button type="button" className="quiet" onClick={() => setCancelling(false)}>
                      Keep it
                    </button>
                  </div>
                </section>
              ) : (
                <div className="panel-actions">
                  {/* Where to go for more comes first, ahead of anything that changes the visit. */}
                  <nav className="panel-links" aria-label="More about this visit">
                    <Link to={`/visits/${v.id}`}>
                      <strong>Open single visit</strong>
                      <span>Details, notes and history</span>
                    </Link>
                    {v.assignment_id > 0 && (
                      <Link to={`/assignments/${v.assignment_id}`}>
                        <strong>
                          Open full assignment
                          {v.assignment_number ? ` ${assignmentRef(v.assignment_number)}` : ''}
                        </strong>
                        <span>The recurring job and its schedule</span>
                      </Link>
                    )}
                  </nav>
                  {/* The one thing to do next, full width: confirm for the lead, then complete. */}
                  {confirmFor ? (
                    <>
                      <button
                        type="button"
                        className="panel-primary"
                        disabled={busy}
                        aria-describedby="confirm-why"
                        onClick={() =>
                          void run(
                            () =>
                              post(`/api/visits/${v.id}/confirm`, { contact_id: confirmFor.id }),
                            `Confirmed for ${confirmFor.name}.`,
                          )
                        }
                      >
                        Confirm for {firstName(confirmFor.name)}
                      </button>
                      <p id="confirm-why" className="panel-hint">
                        When {confirmFor.name} has confirmed by phone or message. The log notes it
                        was the office.
                      </p>
                    </>
                  ) : (
                    v.open &&
                    !(canGive && (needsLead || leadOff)) && (
                      <>
                        <button
                          type="button"
                          className="panel-primary ok-fill"
                          disabled={!v.can_complete || busy}
                          aria-describedby={!v.can_complete ? 'complete-why' : undefined}
                          onClick={() =>
                            void run(() => post(`/api/visits/${v.id}/complete`), 'Visit completed.')
                          }
                        >
                          Mark complete
                        </button>
                        {!v.can_complete && v.complete_hint && (
                          <p id="complete-why" className="panel-hint">
                            {v.complete_hint}
                          </p>
                        )}
                      </>
                    )
                  )}
                  {reopenable && (
                    <>
                      <button
                        type="button"
                        className="panel-primary"
                        disabled={busy || v.reinstate_block !== ''}
                        aria-describedby="reopen-why"
                        onClick={() =>
                          void run(() => post(`/api/visits/${v.id}/reinstate`), 'Visit reopened.')
                        }
                      >
                        Reopen visit
                      </button>
                      <p id="reopen-why" className="panel-hint">
                        {v.reinstate_block ||
                          'Cancelled by mistake? This puts it back on the schedule, charged and paid as usual.'}
                      </p>
                    </>
                  )}
                  {v.open && (
                    <div className="panel-secondary">
                      {confirmFor?.phone && (
                        <a className="button quiet-button" href={`tel:${confirmFor.phone}`}>
                          Call {firstName(confirmFor.name)}
                        </a>
                      )}
                      {confirmFor && v.can_complete && (
                        <button
                          type="button"
                          className="quiet"
                          disabled={busy}
                          onClick={() =>
                            void run(() => post(`/api/visits/${v.id}/complete`), 'Visit completed.')
                          }
                        >
                          Mark complete
                        </button>
                      )}
                      <button type="button" className="danger" onClick={() => setCancelling(true)}>
                        Cancel visit
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/**
 * The people who could take a visit, free ones first, each saying why they are or are not a
 * good pick. One tap gives it to them. Someone off then cannot be picked.
 */
function GiveTo(props: {
  title: string;
  hint: string;
  visit: ScheduleVisit;
  roster: RosterEntry[];
  sameDay: ScheduleVisit[];
  busy: boolean;
  onPick(r: RosterEntry): void;
}) {
  const [find, setFind] = useState('');
  const order = { free: 0, busy: 1, off: 2 } as const;
  const term = find.trim().toLowerCase();
  const people = props.roster
    .map((r) => ({ r, a: availability(r, props.visit, props.sameDay) }))
    .filter(({ r }) => !term || r.name.toLowerCase().includes(term))
    .sort((x, y) => order[x.a.kind] - order[y.a.kind] || x.r.name.localeCompare(y.r.name));
  return (
    <section className="give-to" aria-labelledby="give-to-h">
      <h3 id="give-to-h">{props.title}</h3>
      <p className="panel-hint">{props.hint}</p>
      {props.roster.length > 6 && (
        <input
          type="search"
          className="give-find"
          aria-label="Find a contractor"
          placeholder="Find a contractor"
          value={find}
          onChange={(e) => setFind(e.target.value)}
        />
      )}
      <ul className="give-list">
        {people.map(({ r, a }) => (
          <li key={r.id}>
            <button
              type="button"
              className={`give-option ${a.kind}`}
              disabled={props.busy || a.kind === 'off'}
              onClick={() => props.onPick(r)}
            >
              <span className="give-name">{r.name}</span>
              <span className="give-note">{a.note}</span>
            </button>
          </li>
        ))}
        {people.length === 0 && <li className="muted">Nobody by that name.</li>}
      </ul>
    </section>
  );
}
